import { getAPI } from "../../../api/Networking";
import {
  CategoryNode,
  PaginatedAccounts,
  QuarterOrdersResult,
  SalesPocOption,
} from "../../../models/Dashboard/AccountManagement/AccountManagement";
import { AccountManagementFilters } from "../../../models/Dashboard/AccountManagement/AccountManagementFilters";

export default class AccountManagementAPI {
  private buildAccountsQueryParams = (
    filters: AccountManagementFilters,
  ): URLSearchParams => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.poc_id != null) {
        queryParams.append("poc_id", String(filters.poc_id));
      }
      if (filters.category_id != null) {
        queryParams.append("category_id", String(filters.category_id));
      }
      if (filters.subcategory_id != null) {
        queryParams.append("subcategory_id", String(filters.subcategory_id));
      }
      if (filters.min_ltv != null && !Number.isNaN(filters.min_ltv)) {
        queryParams.append("min_ltv", String(filters.min_ltv));
      }
      if (filters.max_ltv != null && !Number.isNaN(filters.max_ltv)) {
        queryParams.append("max_ltv", String(filters.max_ltv));
      }
      if (filters.year_basis) {
        queryParams.append("year_basis", filters.year_basis);
      }
      if (filters.min_year_rev != null && !Number.isNaN(filters.min_year_rev)) {
        queryParams.append("min_year_rev", String(filters.min_year_rev));
      }
      if (filters.max_year_rev != null && !Number.isNaN(filters.max_year_rev)) {
        queryParams.append("max_year_rev", String(filters.max_year_rev));
      }
      if (filters.last_from) {
        queryParams.append("last_from", filters.last_from.format("YYYY-MM-DD"));
      }
      if (filters.last_to) {
        queryParams.append("last_to", filters.last_to.format("YYYY-MM-DD"));
      }
      if (filters.q.trim()) {
        queryParams.append("q", filters.q.trim());
      }

      queryParams.append("sort_by", filters.sort_by);
      queryParams.append("sort_type", filters.sort_type);
      queryParams.append("page_no", String(filters.page_no));
      queryParams.append("limit", String(filters.limit));

      return queryParams;
    } catch (error) {
      throw error;
    }
  };

  getSalesPocs = async (): Promise<SalesPocOption[]> => {
    try {
      const response = await getAPI("/v1/account-management/sales-pocs");
      return (response.result as SalesPocOption[]) ?? [];
    } catch (error) {
      throw error;
    }
  };

  getCategories = async (): Promise<CategoryNode[]> => {
    try {
      const response = await getAPI("/v1/account-management/categories");
      return (response.result as CategoryNode[]) ?? [];
    } catch (error) {
      throw error;
    }
  };

  getAccounts = async (
    filters: AccountManagementFilters,
  ): Promise<PaginatedAccounts> => {
    try {
      const queryParams = this.buildAccountsQueryParams(filters);
      const response = await getAPI(
        `/v1/dashboards/account-management?${queryParams.toString()}`,
      );
      return response.result as PaginatedAccounts;
    } catch (error) {
      throw error;
    }
  };

  getQuarterOrders = async (params: {
    customer_id: number;
    fy_year: number;
    quarter: number;
  }): Promise<QuarterOrdersResult> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("customer_id", String(params.customer_id));
      queryParams.append("fy_year", String(params.fy_year));
      queryParams.append("quarter", String(params.quarter));

      const response = await getAPI(
        `/v1/dashboards/account-management/quarter-orders?${queryParams.toString()}`,
      );
      return response.result as QuarterOrdersResult;
    } catch (error) {
      throw error;
    }
  };
}
